import React, { useEffect, useState } from "react";
import { api, fmtError } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import CurrencyPicker from "@/components/CurrencyPicker";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";
import { PRICING_MODELS, CREATIVE_TYPES, PLATFORM_TYPES } from "@/pages/CampaignCreate";

/**
 * Modal for editing an existing campaign's core fields.
 * Emits `onSaved(updatedCampaign)` after successful PATCH.
 */
export default function EditCampaignDialog({ campaign, open, onOpenChange, onSaved }) {
    const [form, setForm] = useState({
        name: "",
        start_date: "",
        end_date: "",
        volume: "",
        payout: "",
        revenue: "",
        currency: "USD",
        targeting: "",
        creative_type: "Display",
        pricing_model: "CPM",
        platform_type: "dsp",
        notes: "",
    });
    const [referenceFile, setReferenceFile] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!campaign) return;
        setForm({
            name: campaign.name || "",
            start_date: campaign.start_date || "",
            end_date: campaign.end_date || "",
            volume: campaign.volume ?? "",
            payout: campaign.payout ?? "",
            revenue: campaign.revenue ?? "",
            currency: campaign.currency || "USD",
            targeting: campaign.targeting || "",
            creative_type: campaign.creative_type || "Display",
            pricing_model: campaign.pricing_model || "CPM",
            platform_type: campaign.platform_type || "dsp",
            notes: campaign.notes || "",
        });
        if (campaign.reference_file_id) {
            // Load minimal file metadata to display it (best-effort)
            api.get(`/files/${campaign.reference_file_id}/meta`)
                .then((r) => setReferenceFile({ id: r.data.id, filename: r.data.filename, size: r.data.size }))
                .catch(() => setReferenceFile({ id: campaign.reference_file_id, filename: "Attached file", size: 0 }));
        } else {
            setReferenceFile(null);
        }
    }, [campaign]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Campaign name is required");
            return;
        }
        if (new Date(form.end_date) < new Date(form.start_date)) {
            toast.error("End date must be on or after start date");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: form.name,
                start_date: form.start_date,
                end_date: form.end_date,
                volume: Number(form.volume) || 0,
                payout: Number(form.payout) || 0,
                revenue: Number(form.revenue) || 0,
                currency: form.currency,
                targeting: form.targeting,
                creative_type: form.creative_type,
                pricing_model: form.pricing_model,
                platform_type: form.platform_type,
                notes: form.notes,
                reference_file_id: referenceFile?.id || null,
            };
            const { data } = await api.patch(`/campaigns/${campaign.id}`, payload);
            toast.success("Campaign updated");
            onSaved?.(data);
            onOpenChange?.(false);
        } catch (err) {
            toast.error(fmtError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="edit-campaign-dialog">
                <DialogHeader>
                    <DialogTitle>Edit campaign</DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-5 pt-2">
                    <div>
                        <Label className="overline mb-1.5 block">Campaign name</Label>
                        <Input
                            data-testid="edit-campaign-name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="rounded-none border-slate-300"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="overline mb-1.5 block">Start date</Label>
                            <Input
                                type="date"
                                data-testid="edit-campaign-start-date"
                                value={form.start_date}
                                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                className="rounded-none border-slate-300 font-mono h-10"
                            />
                        </div>
                        <div>
                            <Label className="overline mb-1.5 block">End date</Label>
                            <Input
                                type="date"
                                data-testid="edit-campaign-end-date"
                                value={form.end_date}
                                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                                min={form.start_date}
                                className="rounded-none border-slate-300 font-mono h-10"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="overline mb-1.5 block">Volume</Label>
                            <Input
                                type="number"
                                data-testid="edit-campaign-volume"
                                value={form.volume}
                                onChange={(e) => setForm({ ...form, volume: e.target.value })}
                                className="rounded-none border-slate-300 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="overline mb-1.5 block">Currency</Label>
                            <CurrencyPicker
                                value={form.currency}
                                onChange={(v) => setForm({ ...form, currency: v })}
                                testid="edit-campaign-currency"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="overline mb-1.5 block">Payout</Label>
                            <Input
                                type="number"
                                data-testid="edit-campaign-payout"
                                value={form.payout}
                                onChange={(e) => setForm({ ...form, payout: e.target.value })}
                                className="rounded-none border-slate-300 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="overline mb-1.5 block">Revenue</Label>
                            <Input
                                type="number"
                                data-testid="edit-campaign-revenue"
                                value={form.revenue}
                                onChange={(e) => setForm({ ...form, revenue: e.target.value })}
                                className="rounded-none border-slate-300 font-mono"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="overline mb-1.5 block">Pricing model</Label>
                            <Select value={form.pricing_model} onValueChange={(v) => setForm({ ...form, pricing_model: v })}>
                                <SelectTrigger data-testid="edit-campaign-pricing" className="rounded-none h-10 border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PRICING_MODELS.map((p) => (
                                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="overline mb-1.5 block">Creative type</Label>
                            <Select value={form.creative_type} onValueChange={(v) => setForm({ ...form, creative_type: v })}>
                                <SelectTrigger data-testid="edit-campaign-creative-type" className="rounded-none h-10 border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CREATIVE_TYPES.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="overline mb-1.5 block">Platform</Label>
                        <Select value={form.platform_type} onValueChange={(v) => setForm({ ...form, platform_type: v })}>
                            <SelectTrigger data-testid="edit-campaign-platform-type" className="rounded-none h-10 border-slate-300">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PLATFORM_TYPES.map((p) => (
                                    <SelectItem key={p.value} value={p.value} data-testid={`edit-campaign-platform-type-option-${p.value}`}>{p.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="overline mb-1.5 block">Targeting</Label>
                        <Textarea
                            data-testid="edit-campaign-targeting"
                            value={form.targeting}
                            onChange={(e) => setForm({ ...form, targeting: e.target.value })}
                            className="rounded-none border-slate-300"
                            rows={2}
                        />
                    </div>
                    <div>
                        <Label className="overline mb-1.5 block">Notes</Label>
                        <Textarea
                            data-testid="edit-campaign-notes"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="rounded-none border-slate-300"
                            rows={3}
                        />
                    </div>
                    <div>
                        <Label className="overline mb-1.5 block">Reference brief / image</Label>
                        <FileUploader
                            value={referenceFile}
                            onChange={setReferenceFile}
                            accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx"
                            maxSizeMb={50}
                            testid="edit-campaign-reference-file"
                        />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange?.(false)}
                            className="rounded-none border-slate-300"
                            data-testid="edit-campaign-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={busy}
                            className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="edit-campaign-submit"
                        >
                            {busy ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
